import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { DateService } from '@rd/core/services/date/date.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { NydusNetworkDataError } from '@rd/errors/nydus-network-data-error';
import {
  giftCardResponseSchema,
  giftCardShowResponseSchema,
} from '@rd/schemas/gift-card.schema';
import {
  GiftCard,
  GiftCardCategory,
  GiftCardDenomination,
  GiftCardSort,
  GiftCardType,
  PointsTransaction,
} from '@rd/types';
import { GiftCardOrderResponse } from '@rd/types/gift-card-order.response';
import { GiftCardModel, GiftCardShowModel } from '@rd/types/gift-card.model';
import {
  GiftCardResponse,
  GiftCardShowResponse,
} from '@rd/types/gift-card.response';
import { PointsTransactionModel } from '@rd/types/points-transaction.model';
import { isValidUrl } from '@rd/utils/url.utils';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
interface GiftCardFilterParams {
  filterCategory: string | 'all';
  filterType: string | 'all';
  filterKeyword: string;
  sortType: string | 'name';
}

interface OrderPostParams {
  giftCardsOrder: {
    recipient: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      message: string;
    };
    displayValue: GiftCardDenomination['displayValue'];
  };
}

@Injectable({
  providedIn: 'root',
})
export class GiftCardApiService {
  private apiValidationService = inject(ApiValidationService);
  private nydusNetworkApiService = inject(NydusNetworkApiDataService);
  private sentryService = inject(SentryService);
  private dateService = inject(DateService);
  private urlUtilsService = inject(UrlUtilsService);

  getGiftCard(giftCardId: string): Observable<GiftCard> {
    return this.nydusNetworkApiService
      .get<GiftCardShowResponse>(`gift_cards/${giftCardId}`)
      .pipe(
        tap((response) =>
          this.apiValidationService.validate<GiftCardShowResponse>({
            apiName: `gift_cards/${giftCardId}`,
            schema: giftCardShowResponseSchema,
            response,
          }),
        ),
        map((giftCardResponse) => {
          return this.transformGiftCardShowResponse(giftCardResponse);
        }),
      );
  }

  getGiftCards(params?: HttpParams): Observable<GiftCard[]> {
    return this.nydusNetworkApiService
      .get<GiftCardResponse>('gift_cards', params)
      .pipe(
        tap((response) =>
          this.apiValidationService.validate<GiftCardResponse>({
            apiName: `gift_cards?${decodeURI(params?.toString() || '')}`,
            schema: giftCardResponseSchema,
            response,
          }),
        ),
        map((giftCardResponse) => {
          return this.transformGiftCardResponse(giftCardResponse);
        }),
      );
  }

  private transformGiftCardShowResponse(
    giftCardResponse: GiftCardShowResponse,
  ): any {
    try {
      return this.transformGiftCardModelFromShowResponse(giftCardResponse.data);
    } catch {
      const nydusNetworkDataError = new NydusNetworkDataError({
        httpMethod: 'GET',
        endpoint: '/gift_cards',
        message: 'Gift card show response have discrepancy.',
      });
      this.sentryService.manualError(nydusNetworkDataError.toString());
    }
  }

  getPaginatedGiftCards(params: {
    page: number;
    pageSize: number;
    filterCategory: GiftCardCategory | 'all';
    filterType: GiftCardType | 'all';
    filterKeyword: string;
    sortType: GiftCardSort;
  }): Observable<{ giftCards: GiftCard[]; pageCount: number }> {
    const { page, pageSize } = params;

    const filterParams = this.buildPaginatedParams(params);

    return this.nydusNetworkApiService
      .getPaginated<GiftCardResponse>(
        'gift_cards',
        page,
        pageSize,
        filterParams,
      )
      .pipe(
        tap((response) => {
          if (response?.body) {
            this.apiValidationService.validate<GiftCardResponse>({
              apiName: `gift_cards?page[number]=${page}&page[size]=${pageSize}&${decodeURI(
                filterParams.toString(),
              )}`,
              schema: giftCardResponseSchema,
              response: response.body,
            });
          }
        }),
        map((response) => {
          // @ts-ignore
          const data = this.transformGiftCardResponse(response?.body);
          return {
            giftCards: data,
            pageCount: response.pageCount,
          };
        }),
      );
  }

  postOrder(
    giftCardDenominationId: GiftCardDenomination['id'],
    orderPostParams: OrderPostParams,
  ): Observable<PointsTransaction> {
    return this.nydusNetworkApiService
      .post<GiftCardOrderResponse>(
        `gift_cards/${giftCardDenominationId}/orders`,
        {
          ...orderPostParams,
        },
      )
      .pipe(
        map((giftCardOrderPostResponse) => {
          return this.transformGiftCardOrderResponse(giftCardOrderPostResponse);
        }),
      );
  }

  private transformGiftCardOrderResponse(
    giftCardOrderPostResponse: GiftCardOrderResponse,
  ): PointsTransaction {
    const { data } = giftCardOrderPostResponse;
    return this.transformPointsTransactionModel(data);
  }

  // TODO: centralize all points activity model transformations into one service
  private transformPointsTransactionModel(
    pointsTransactionModel: PointsTransactionModel,
  ): PointsTransaction {
    const { id, attributes, relationships } = pointsTransactionModel;
    const {
      amount,
      balanceAsOf,
      category,
      description,
      status,
      orderFulfillmentStatus,
      productType,
      createdAt,
      transactionTime,
      metadata,
      orderItemData,
      entryType,
    } = attributes;

    const transformedPointsTransaction = {
      id,
      amount,
      balance: balanceAsOf,
      category,
      descriptor: description,
      status,
      orderFulfillmentStatus,
      productType,
      createdAt: this.dateService.parseDate(createdAt),
      transactionTime: this.dateService.parseDate(transactionTime),
      metadata,
      orderItemData,
      entryType,
    };

    const checkedPointsTransaction = this.checkOrderId(
      transformedPointsTransaction,
      relationships,
    );

    return checkedPointsTransaction;
  }

  private buildPaginatedParams(
    filterArguments: GiftCardFilterParams,
  ): HttpParams {
    const { filterCategory, filterKeyword, filterType, sortType } =
      filterArguments;

    let filterParams = new HttpParams().set('search[name]', filterKeyword);

    // NN requirements:
    // NOTE: the following operators ONLY work for GC.
    // $any to perform OR operation condition for multiple types input.
    // $contain to perform AND operation condition for multiple types input.

    if (filterCategory !== 'all') {
      /**
       * NOTE(@SonTruongAscenda):
       *
       * For Hyundai RC migration, Hyundai's Department Store cards now use the 'malls_and_hypermarkets' category.
       * To prevent missing cards during the transition, we need to send both 'malls_and_hypermarkets' and 'department_store'
       * values to Alfred under the Department Store category in Hyundai RD.
       *
       * @see https://kaligo.slack.com/archives/CUSBMUU3H/p1746760607811699?thread_ts=1741933272.841689&cid=CUSBMUU3H
       */
      if (filterCategory === 'department_store') {
        filterParams = filterParams.append(
          'filter[categories][$any][]',
          'department_store',
        );
        filterParams = filterParams.append(
          'filter[categories][$any][]',
          'malls_and_hypermarkets',
        );
      } else {
        filterParams = filterParams.set(
          'filter[categories][$any]',
          filterCategory,
        );
      }
    }

    if (filterType !== 'all') {
      filterParams = filterParams.set('filter[displayTypes][$any]', filterType);
    }

    if (sortType !== 'name') {
      filterParams = filterParams.set('sort', sortType);
    }

    return filterParams;
  }

  private transformGiftCardResponse(
    giftCardResponse: GiftCardResponse,
  ): GiftCard[] {
    // @ts-ignore
    return giftCardResponse.data.map((giftCardModel) =>
      this.transformGiftCardModel(giftCardModel),
    );
  }

  private transformGiftCardModel(giftCardModel: GiftCardModel): GiftCard {
    const { id, attributes } = giftCardModel;
    const {
      categories,
      displayTypes,
      denominations,
      imageUrls: apiImageUrls,
      logoUrl,
      maxDiscountPercentage,
      name,
      shortDescription,
    } = attributes;

    return {
      id,
      categories,
      displayTypes,
      denominations,
      imageUrls: this.addPlaceholderIfNoImageUrls(apiImageUrls),
      // @ts-ignore
      logoUrl: isValidUrl(logoUrl) ? new URL(logoUrl) : undefined,
      name,
      maxDiscountPercentage,
      shortDescription,
    };
  }

  private transformGiftCardModelFromShowResponse(
    giftCardModel: GiftCardShowModel,
  ): GiftCard {
    const { id, attributes } = giftCardModel;
    const {
      categories,
      displayTypes,
      deliveryMethod,
      denominations,
      description,
      imageUrls: apiImageUrls,
      logoUrl,
      maxDiscountPercentage,
      name,
      redemptionChannel,
      redemptionInstructions,
      shortDescription,
      termsAndConditions,
      termsAndConditionsUrl,
      validityInDays,
    } = attributes;
    return {
      id,
      categories,
      displayTypes,
      deliveryMethod,
      denominations,
      description,
      imageUrls: this.addPlaceholderIfNoImageUrls(apiImageUrls),
      // @ts-ignore
      logoUrl: isValidUrl(logoUrl) ? new URL(logoUrl) : undefined,
      maxDiscountPercentage,
      name,
      redemptionChannel,
      redemptionInstructions,
      shortDescription,
      termsAndConditions,
      // @ts-ignore
      termsAndConditionsUrl,
      validityInDays,
    };
  }

  private checkOrderId(
    pointsTransactionTransformed: PointsTransaction,
    relationships: any,
  ): PointsTransaction {
    if (relationships && relationships.order) {
      const relationshipsUrl = relationships.order.links.related;
      const orderId = relationshipsUrl.slice(
        relationshipsUrl.lastIndexOf('/') + 1,
        relationshipsUrl.length,
      );

      return {
        ...pointsTransactionTransformed,
        id: orderId,
      };
    }

    return pointsTransactionTransformed;
  }

  // There'es a possibilty that a gift card might have no imageUrls, or have null in the array.
  // Add a placeholder for those cases.
  private addPlaceholderIfNoImageUrls(
    imageUrls: GiftCard['imageUrls'],
  ): GiftCard['imageUrls'] {
    const nonNullImageUrls = imageUrls.filter(
      (imageUrl: string | null) => !!imageUrl,
    );

    if (nonNullImageUrls.length === 0) {
      const PLACEHOLDER_IMAGE = this.urlUtilsService.imageUrl(
        '/images/placeholder.png',
      );
      return [PLACEHOLDER_IMAGE];
    }

    return imageUrls;
  }
}
